// src/components/auth/PartnerSignup.jsx - Rebuilt with shadcn components
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  KeyRound,
  User,
  Mail,
  Shield,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Sparkles,
  UserCheck
} from 'lucide-react';

// Import shadcn components
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Field, FieldLabel, FieldDescription } from '../ui/field';
import { PartnerCodeInput, Input, PasswordInput } from '../ui';
import { Alert, AlertDescription } from '../ui/alert';

// Import the real auth context
import { useAuth } from '../../contexts/AuthContext.jsx';

const PartnerSignup = () => {
  // Step management
  const [step, setStep] = useState(1); // 1: Code Entry, 2: Account Setup, 3: Success
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  
  // Auth context
  const { error, verifyPartnerCode, completePartnerSetup, clearError } = useAuth();
  
  // Form data
  const [partnerCode, setPartnerCode] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const navigate = useNavigate();

  // Step 1: Verify Partner Code
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();

    try {
      const businessData = await verifyPartnerCode(partnerCode);
      if (businessData) {
        setBusinessInfo(businessData);
        setStep(2);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Create Admin Account
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationError('');
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);
    clearError();

    try {
      const success = await completePartnerSetup(
        partnerCode,
        formData.name,
        formData.email,
        formData.password
      );

      if (success) {
        setStep(3);
        // Auto redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md">
        {/* Step 1: Partner Code Entry */}
        {step === 1 && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mb-4">
                <KeyRound className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-white">Join as Partner</CardTitle>
              <CardDescription className="text-slate-400">
                Enter your partner code to get started
              </CardDescription>
            </CardHeader>

            <CardContent>
              {/* Error Display */}
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Code Entry Form */}
              <form onSubmit={handleCodeSubmit} className="space-y-6">
                <Field>
                  <FieldLabel htmlFor="partnerCode">Partner Code *</FieldLabel>
                  <PartnerCodeInput
                    id="partnerCode"
                    value={partnerCode}
                    onChange={(e) => setPartnerCode(e.target.value)}
                    placeholder="ABC-12345"
                    required
                    disabled={isLoading}
                  />
                  <FieldDescription>
                    Format: XXX-XXXXX (provided by your platform administrator)
                  </FieldDescription>
                </Field>

                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={isLoading || partnerCode.length < 9}
                >
                  {isLoading ? 'Verifying...' : (
                    <>
                      Verify Code
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-slate-900/40 px-2 text-slate-400">Already have an account?</span>
                </div>
              </div>

              <Button
                asChild
                variant="outline"
                className="w-full"
                disabled={isLoading}
              >
                <Link to="/login">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Link>
              </Button>
            </CardContent>
          </>
        )}

        {/* Step 2: Account Setup */}
        {step === 2 && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-4">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-white">Create Your Account</CardTitle>
              {businessInfo && (
                <div className="mt-4 inline-flex items-center px-4 py-2 bg-emerald-600/20 border border-emerald-600/30 rounded-full">
                  <span className="text-emerald-400 font-medium">{businessInfo.name}</span>
                </div>
              )}
            </CardHeader>

            <CardContent>
              {/* Error Display */}
              {(error || validationError) && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{validationError || error}</AlertDescription>
                </Alert>
              )}

              {/* Account Setup Form */}
              <form onSubmit={handleAccountSubmit} className="space-y-6">
                {/* Name Field */}
                <Field>
                  <FieldLabel htmlFor="name">Full Name *</FieldLabel>
                  <Input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    placeholder="Enter your full name"
                    leftIcon={User}
                    required
                    disabled={isLoading}
                  />
                </Field>

                {/* Email Field */}
                <Field>
                  <FieldLabel htmlFor="email">Email Address *</FieldLabel>
                  <Input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    placeholder="Enter your email"
                    leftIcon={Mail}
                    required
                    disabled={isLoading}
                  />
                </Field>

                {/* Password Field */}
                <Field>
                  <FieldLabel htmlFor="password">Password *</FieldLabel>
                  <PasswordInput
                    id="password"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    placeholder="Create a password"
                    required
                    disabled={isLoading}
                  />
                </Field>

                {/* Confirm Password Field */}
                <Field>
                  <FieldLabel htmlFor="confirmPassword">Confirm Password *</FieldLabel>
                  <PasswordInput
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    placeholder="Confirm your password"
                    leftIcon={Shield}
                    required
                    disabled={isLoading}
                  />
                </Field>

                {/* Form Actions */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    disabled={isLoading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>

                  <Button
                    type="submit"
                    variant="default"
                    disabled={isLoading || !formData.name || !formData.email || !formData.password || !formData.confirmPassword}
                    className="flex-1"
                  >
                    {isLoading ? 'Creating Account...' : (
                      <>
                        Create Account
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-white text-2xl">Welcome Aboard!</CardTitle>
              <CardDescription className="text-slate-300 text-base mt-4">
                Your partner account has been successfully created. You'll be redirected to your dashboard in a few seconds.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="flex items-center justify-center text-slate-400 text-sm">
                <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                Redirecting to dashboard...
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default PartnerSignup;